import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Input } from "@material-tailwind/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPhone, faEnvelope, faCalendarAlt, faLink, faTimes, faSave, faEdit } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import Select from 'react-select';
import { fixResizeObserverError } from '../../../utils/resizeObserverFix';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';

const OwnerModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  ownerstatus = [],
  statusLoading = false
}) => {
  const { user } = useAuth();
  const { rootUrl } = require('../../../config/config');
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    date_joined: "",
    drive_link: "",
    status_id: "",
    is_invoiced: true
  });
  const [errorAlert, setErrorAlert] = useState({ open: false, message: '' });

  useEffect(() => {
    return fixResizeObserverError();
  }, []);

  useEffect(() => {
    if (editData) {
      console.log('EditData received:', editData);
      console.log('EditData fields:', Object.keys(editData));
      console.log('Status field value:', editData.status_id, editData.status);

      setFormData({
        name: editData.name || "",
        phone: editData.phone || "",
        email: editData.email || "",
        date_joined: editData.date_joined ? editData.date_joined.split('T')[0] : "",
        drive_link: editData.drive_link || "",
        status_id: editData.status_id || editData.status || "",
        is_invoiced: editData.is_invoiced !== undefined ? editData.is_invoiced : true
      });
    } else {
      setFormData({
        name: "",
        phone: "",
        email: "",
        date_joined: "",
        drive_link: "",
        status_id: "",
        is_invoiced: true
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for name field - convert to uppercase
    if (name === 'name') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleStatusChange = (option) => {
    setFormData(prev => ({
      ...prev,
      status_id: option ? option.value : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Name is required");
      return;
    }
    if (!formData.phone.trim()) {
      alert("Phone is required");
      return;
    }
    if (!formData.status_id) {
      alert("Status is required");
      return;
    }

    const submissionData = {
      ...formData,
      input_by: editData ? undefined : user.username,
      edit_by: editData ? user.username : undefined
    };

    // Remove undefined fields
    Object.keys(submissionData).forEach(key => submissionData[key] === undefined && delete submissionData[key]);

    try {
      const url = editData
        ? `${rootUrl}/owners/${editData.code}`
        : `${rootUrl}/owners`;

      const method = editData ? 'put' : 'post';

      console.log('API Call Details:');
      console.log('URL:', url);
      console.log('Method:', method);
      console.log('Form Data:', submissionData);
      console.log('Edit Data:', editData);

      const response = await axios[method](url, submissionData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 200 || response.status === 201) {
        onSuccess();
        onClose();
        setFormData({
          name: "",
          phone: "",
          email: "",
          date_joined: "",
          drive_link: "",
          status_id: ""
        });
      }
    } catch (error) {
      console.error('Error saving owner:', error);

      // Handle different types of errors
      let errorMessage = 'Error saving owner. Please try again.';

      if (error.response) {
        // Server responded with error status
        if (error.response.status === 400 && error.response.data) {
          if (error.response.data.validData && error.response.data.validData.error) {
            // Show specific validation error
            errorMessage = error.response.data.validData.error;
          } else if (error.response.data.message) {
            // Show general error message
            errorMessage = error.response.data.message;
          }
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection.';
      }

      setErrorAlert({ open: true, message: errorMessage });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {/* Error Alert */}

      <Card className="bg-white rounded-2xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Typography variant="h4" className="text-gray-800 font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={editData ? faEdit : faUser} className="text-blue-500" />
            {editData ? 'Edit Owner' : 'Add New Owner'}
          </Typography>
          <Button
            variant="text"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>

        {/* Error Alert */}
        <Alerty
          open={errorAlert.open}
          setOpen={(open) => setErrorAlert(prev => ({ ...prev, open }))}
          color="red"
          message={errorAlert.message}
          variant="filled"
        />

        {/* Owner ID Display (Edit Mode Only) */}
        {editData && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <Typography variant="small" className="text-gray-600 font-medium">
              Owner ID: <span className="font-mono text-blue-600">{editData.code}</span>
            </Typography>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div>
              <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-blue-500" />
                Full Name *
              </Typography>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name (will be converted to uppercase)"
                className="w-full"
                required
                icon={<FontAwesomeIcon icon={faUser} />}
              />
            </div>

            {/* Phone Field */}
            <div>
              <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faPhone} className="text-blue-500" />
                Phone Number *
              </Typography>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="w-full"
                required
                icon={<FontAwesomeIcon icon={faPhone} />}
              />
            </div>

            {/* Email Field */}
            <div>
              <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faEnvelope} className="text-blue-500" />
                Email Address
              </Typography>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                className="w-full"
                icon={<FontAwesomeIcon icon={faEnvelope} />}
              />
            </div>

            {/* Date Joined Field */}
            <div>
              <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-500" />
                Date Joined
              </Typography>
              <Input
                type="date"
                name="date_joined"
                value={formData.date_joined}
                onChange={handleChange}
                className="w-full"
                icon={<FontAwesomeIcon icon={faCalendarAlt} />}
              />
            </div>

            {/* Status Field */}
            <div>
              <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} className="text-blue-500" />
                Status *
              </Typography>
              <Select
                name="status_id"
                options={Array.isArray(ownerstatus) ? ownerstatus.map((status) => ({
                  value: status.id,
                  label: status.name.toUpperCase()
                })) : []}
                value={formData.status_id ? {
                  value: formData.status_id,
                  label: Array.isArray(ownerstatus)
                    ? ownerstatus.find(s => s.id === parseInt(formData.status_id))?.name.toUpperCase() || 'Select Status'
                    : 'Select Status'
                } : null}
                onChange={handleStatusChange}
                isLoading={statusLoading}
                isDisabled={statusLoading}
                placeholder="Select Status"
                isClearable={false}
                isSearchable={true}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '40px',
                    borderRadius: '0.5rem',
                    borderColor: '#d1d5db',
                    '&:hover': {
                      borderColor: '#9ca3af'
                    },
                    '&:focus-within': {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                    color: state.isSelected ? 'white' : '#1f2937'
                  })
                }}
              />
            </div>

            {/* Empty div for grid alignment */}
            <div></div>
          </div>

          {/* Drive Link Field - Full Width */}
          <div>
            <Typography variant="small" className="text-gray-700 font-medium mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faLink} className="text-blue-500" />
              Drive Link
            </Typography>
            <Input
              type="url"
              name="drive_link"
              value={formData.drive_link}
              onChange={handleChange}
              placeholder="Enter Google Drive link"
              className="w-full"
              icon={<FontAwesomeIcon icon={faLink} />}
            />
            <Typography variant="small" className="text-gray-500 mt-1">
              Optional: Link to owner's documents in Google Drive
            </Typography>
          </div>

          {/* Invoicing Status Checkbox - Full Width */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_invoiced"
                name="is_invoiced"
                checked={formData.is_invoiced}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    is_invoiced: e.target.checked
                  }));
                }}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="is_invoiced" className="cursor-pointer">
                <Typography variant="small" className="text-gray-700 font-medium">
                  Invoiceable Owner
                </Typography>
                <Typography variant="small" className="text-gray-500">
                  Enable this to allow generating invoices for this owner
                </Typography>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {editData ? 'Update Owner' : 'Add Owner'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={onClose}
              className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faTimes} />
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default OwnerModal; 